/**
 * EventBridge event types for Sitescene platform
 */

import type { Event, GeneratedEventSummary } from "./event";
import type { EventHistorySource } from "./event-history";
import type { GeoJSONPoint } from "./map";
import type { NotificationChannel } from "./notification";
import type { PipelineType } from "./pipeline";
import type { MeasurementVerification } from "./plausibility";
import type { SensorDataType, SensorFormat } from "./raw-sensor-data";

import { EventType } from "./event";
import {
	EnhancedAnalysisType,
	type EnhancedResult,
	type Measurement,
} from "./measurement";

// Video processing event sources
export const VIDEO_EVENT_SOURCE = "raw.video.subscriber";
export const VIDEO_PROCESSOR_EVENT_SOURCE = "raw.video.processor";
export const VLM_EVENT_SOURCE = "vlm.service";

// Event aggregator event source
export const EVENT_AGGREGATOR_SOURCE = "event.aggregator";

// Motion detection event source
export const MOTION_DETECTION_SOURCE = "motion.detection";

// Weapons detection event source (GPU-based YOLOv8 detection)
export const WEAPONS_DETECTION_SOURCE = "weapons_detection.service";

// Rekognition service event source (AWS Rekognition-based weapons detection)
export const REKOGNITION_SOURCE = "rekognition.service";

// Notification service event source
export const NOTIFICATION_SOURCE = "notification.service";

// Scene description: on-demand, single-sensor generation (sitescene#460).
// Distinct from the hourly cron, which sweeps closed sampling windows. This
// event covers the two cases that must not wait for the next tick: a camera's
// very first clip, and an operator pressing Regenerate.
export const SCENE_DESCRIPTION_SOURCE = "scene.description.service";
export const SCENE_DESCRIPTION_REQUESTED_DETAIL_TYPE =
	"scene-description.requested";

// Scene facts: draft one SceneFactProposal in the queue worker. Drafting sends
// a clip to Gemini and can outlast API Gateway's ~30 s limit, so the HTTP
// request only inserts the `drafting` row and emits this.
export const SCENE_FACTS_SOURCE = "scene.facts.service";
export const SCENE_FACTS_REQUESTED_DETAIL_TYPE = "scene-facts.requested";

// Notification detail type constants
export const EMAIL_NOTIFICATION_DETAIL_TYPE = "notification.email";
export const SMS_NOTIFICATION_DETAIL_TYPE = "notification.sms";

// Unified email queue constants
export const EMAIL_DETAIL_TYPE = "email.send";
export const EMAIL_SOURCE = "email.service";

// Video processing detail type constants
export const VIDEO_READY_DETAIL_TYPE = "video.ready";
export const VIDEO_REQUIRES_PREPROCESSING_DETAIL_TYPE =
	"video.requiresPreProcessing";
// Motion detection detail type constants
export const VIDEO_MOTION_DETAIL_TYPE = "video.motion";
export const VIDEO_NO_MOTION_DETAIL_TYPE = "video.no_motion";

// Event lifecycle detail type constants
export const EVENT_CREATED_DETAIL_TYPE = "event.created";
export const EVENT_UPDATED_DETAIL_TYPE = "event.updated";

// The only detail types the dashboard WebSocket broadcaster sends. Its
// EventBridge rule matches just these, so no other bus event starts the
// Lambda (and opens a Mongo connection) only to be dropped.
export const DASHBOARD_BROADCAST_DETAIL_TYPES = [
	EVENT_CREATED_DETAIL_TYPE,
	EVENT_UPDATED_DETAIL_TYPE,
] as const;

// Bus sources the observability WebSocket never shows: the work-queue events
// of cross-camera tracking and the Vision lab. A track asks for every clip in
// its window at once (130 events in one minute on sandbox, 2026-11-02), and
// each one started its own broadcaster Lambda with its own Mongo connection,
// enough to fill the cluster's connection limit. The values match
// TRACKING_SOURCE, AI_TRACK_SOURCE and VISION_SOURCE.
export const OBSERVABILITY_EXCLUDED_SOURCES = [
	"tracking.service",
	"ai-track.service",
	"vision.service",
] as const;

// Evaluation tool detail types + sources.
// Eval jobs use a DISTINCT detail type so they never match the production
// AnomalyProcessing/BoloProcessing subscriptions (which match "video.motion").
// Eval output uses a distinct source so it never hits the tenant
// measurement/event/notification chain.
export const EVAL_JOB_DETAIL_TYPE = "eval.video.motion";
export const EVAL_JOB_SOURCE = "eval.service";
export const EVAL_MEASUREMENT_DETAIL_TYPE = "eval.measurement";
export const EVAL_USAGE_DETAIL_TYPE = "eval.usage";
// Terminal failure for one eval job. Emitted instead of eval.usage when the
// worker hits a permanent failure (e.g. the source clip is missing from S3) so
// the result is marked Failed rather than orphaned in Pending forever.
export const EVAL_FAILURE_DETAIL_TYPE = "eval.failure";
export const EVAL_RESULT_SOURCE = "pipeline.eval";

// Weapons detection detail type constants
export const WEAPON_DETECTED_DETAIL_TYPE = "weapon.detected";
export const WEAPON_NO_DETECTION_DETAIL_TYPE = "weapon.no_detection";

// Raw sensor data ingestion event source + detail type.
// Emitted after a raw on-prem payload is persisted, so type-specific
// processors (e.g. air quality) can normalize it. Routed by `detail.sensorType`.
export const RAW_SENSOR_DATA_SOURCE = "raw.sensor-data";
export const SENSOR_DATA_RECORDED_DETAIL_TYPE = "sensor-data.recorded";

// Air-quality evaluator source. Emits measurement.new on a threshold/delta
// breach; must be on the MeasurementProcessing subscription's source allow-list.
export const AIR_QUALITY_EVALUATOR_SOURCE = "air_quality.evaluator";

export interface AlertNotificationEmailEvent extends NotificationEvent {
	emailType: "alert-notification";
	idempotencyKey: string;
}

/**
 * Detected weapon information for weapon detection events
 */
export interface DetectedWeapon {
	/** Confidence score (0-1) for this detection */
	confidence: number;
	/** Type of weapon detected */
	label: "firearm" | "knife";
}

export type EmailEvent =
	| AlertNotificationEmailEvent
	| ExportCompleteEmailEvent
	| InviteUserEmailEvent
	| UserUpdatedEmailEvent;

export type EmailType =
	| "alert-notification"
	| "export-complete"
	| "invite-user"
	| "user-updated";

/**
 * Event payload for event.created events
 * Emitted when a new Event is formed by the event aggregator
 */
export interface EventCreatedEvent {
	/**
	 * Watch ids newly attributed to the event by this operation. Drives the
	 * watch alert counter; omitted when empty so the bus filter only delivers
	 * events that matched a watch.
	 */
	addedWatches?: string[];
	/** Geographic centroid of the event */
	centroid: GeoJSONPoint;
	/** Whether a collision was confirmed by enhanced analysis */
	collisionConfirmed?: boolean;
	/** AI-generated description of the event */
	description: string;
	/** ISO timestamp when the event ended */
	endTimestamp: string;
	/** Whether a fire/electrical hazard was confirmed by enhanced analysis */
	fireConfirmed?: boolean;
	/** Type of fire detected (electrical, chemical, structural, unknown) */
	fireType?: string;
	/** Unique identifier of the created event */
	id: string;
	/** Installation identifier (used for tenant lookup in broadcast handler) */
	installationId: string;
	/** Array of measurement IDs included in this event */
	measurements: string[];
	/**
	 * Whether the event still holds only a third party's assertion, with no
	 * analysis of ours. Drives the "Preliminary Data" caption; see
	 * `Event.preliminary`.
	 */
	preliminary?: boolean;
	/** Severity score (1-10) derived from highest measurement severity */
	severity: number;
	/** ISO timestamp when the event started */
	startTimestamp: string;
	/** Current status of the event */
	status: string;
	/** Whether a threat was confirmed by enhanced analysis */
	threatConfirmed?: boolean;
	/** AI-generated title of the event */
	title: string;
	/** Classified event types (Safety, Security, Operations) */
	types: EventType[];
	/** Full set of watch ids this event matches (for display/badges). */
	watches?: string[];
	/** Whether a weapon was detected by enhanced analysis */
	weaponDetected?: boolean;
	/** Type of weapon detected (e.g., "firearm", "knife") */
	weaponType?: string;
}

/**
 * Full EventBridge event structure for EventCreated
 */
export interface EventCreatedEventBridgeEvent {
	detail: EventCreatedEvent;
	"detail-type": typeof EVENT_CREATED_DETAIL_TYPE;
	source: typeof EVENT_AGGREGATOR_SOURCE;
}

/**
 * Helper type to create an Event from EventCreatedEvent
 * Used for type compatibility when transforming events
 */
export type EventFromCreatedEvent = Omit<Event, "createdAt" | "updatedAt">;

/**
 * Event payload for event.updated events
 * Emitted when measurements are added or summary is regenerated
 */
export interface EventUpdatedEvent {
	/** userId of the person who made the change; absent for system-originated updates */
	actor?: string;
	/**
	 * Resolved display name of the actor, so the async history subscriber does not
	 * have to call Kinde again. It runs in a separate SQS-triggered Lambda with no
	 * JWT — `getAuthorizerClaims` returns null there, so its `ClientContext` is
	 * empty — which makes this message the only way to hand it the name. Absent or
	 * empty means the subscriber resolves the name itself.
	 *
	 * NOT a private channel to that subscriber. This payload is also broadcast to
	 * tenant dashboard clients over the WebSocket and read by the watch-alert
	 * counter, so treat anything added here as client-visible.
	 */
	actorName?: string;
	/**
	 * Watch ids newly attributed to the event by this operation. Drives the
	 * watch alert counter; omitted when empty.
	 */
	addedWatches?: string[];
	/**
	 * Carried from a backfill verification's `measurement.updated`. The event
	 * may be weeks old, so auto-notification skips it however severity moved.
	 */
	backfill?: boolean;
	/** Updated geographic centroid of the event */
	centroid: GeoJSONPoint;
	/** Changed fields; present only for user-initiated updates */
	changes?: Record<string, unknown>;
	/** Whether a collision was confirmed by enhanced analysis */
	collisionConfirmed?: boolean;
	/** Updated AI-generated description */
	description: string;
	/** Updated end timestamp */
	endTimestamp: string;
	/** Whether a fire/electrical hazard was confirmed by enhanced analysis */
	fireConfirmed?: boolean;
	/** Type of fire detected (electrical, chemical, structural, unknown) */
	fireType?: string;
	/** Unique identifier of the updated event */
	id: string;
	/** Installation identifier (used for tenant lookup in broadcast handler) */
	installationId: string;
	/** Updated array of measurement IDs */
	measurements: string[];
	/**
	 * Whether the event still holds only a third party's assertion, with no
	 * analysis of ours. Drives the "Preliminary Data" caption; see
	 * `Event.preliminary`.
	 */
	preliminary?: boolean;
	/**
	 * The event's severity immediately before this update.
	 *
	 * Set only by the aggregator paths that can change severity on their own
	 * (a measurement joining, or enhancement recalculating it). Auto-notification
	 * reads it to alert on an escalation the operator has not been told about
	 * yet: an event created below everyone's threshold and later ratcheted above
	 * one would otherwise never notify, because notifications evaluate severity
	 * once and `event.created` fires only at creation.
	 *
	 * Absent means "not a severity-bearing update" — user edits and status
	 * changes omit it, and auto-notification ignores those rather than alerting
	 * someone about their own edit.
	 */
	previousSeverity?: number;
	/** Updated severity score */
	severity: number;
	/**
	 * What made a System update (`actor: "system"`): the claims check, the
	 * verifier, a staff reanalysis, or more than one. Recorded on the history
	 * entry for staff. Like everything here it also reaches dashboard clients;
	 * it is a label, nothing sensitive.
	 */
	sources?: EventHistorySource[];
	/** Start timestamp (unchanged) */
	startTimestamp: string;
	/** Current status of the event */
	status: string;
	/** Whether a threat was confirmed by enhanced analysis */
	threatConfirmed?: boolean;
	/** Updated AI-generated title */
	title: string;
	/** Classified event types (Safety, Security, Operations) */
	types: EventType[];
	/** Type of update that occurred */
	updateType:
		| "measurement_added"
		| "measurement_enhanced"
		/**
		 * A staff reanalysis rewrote (or reverted) member measurements. Never
		 * notifies, whichever way severity moved: the event may be hours old.
		 */
		| "measurement_reanalyzed"
		/** Plausibility verification rewrote a member measurement's severity. */
		| "measurement_verified"
		| "status_changed"
		| "summary_regenerated"
		| "user_updated";
	/** Full set of watch ids this event matches (for display/badges). */
	watches?: string[];
	/** Whether a weapon was detected by enhanced analysis */
	weaponDetected?: boolean;
	/** Type of weapon detected (e.g., "firearm", "knife") */
	weaponType?: string;
}

/**
 * Full EventBridge event structure for EventUpdated
 */
export interface EventUpdatedEventBridgeEvent {
	detail: EventUpdatedEvent;
	"detail-type": typeof EVENT_UPDATED_DETAIL_TYPE;
	source: typeof EVENT_AGGREGATOR_SOURCE;
}

export interface ExportCompleteEmailEvent {
	dashboardUrl: string;
	emailType: "export-complete";
	eventCount: number;
	exportId: string;
	idempotencyKey: string;
	reason: string;
	tenantId: string;
	userEmail: string;
}

export interface InviteUserEmailEvent {
	dashboardUrl: string;
	emailType: "invite-user";
	idempotencyKey: string;
	organizationName: string;
	recipientName: string;
	tenantId: string;
	userEmail: string;
}

/**
 * Event payload for measurement.created events
 * Emitted by measurement-subscriber after successful persistence
 */
export interface MeasurementCreatedEvent {
	/** Optional description of what was detected */
	description?: string;
	/** ISO timestamp when the detected event ended */
	endTimestamp: string;
	/** Camera rules the first pass flagged as looking met (`Measurement.firstPassRuleFlags`). */
	firstPassRuleFlags?: string[];
	/** Unique identifier of the persisted measurement */
	id: string;
	/** Installation identifier */
	installationId: string;
	/** Classification labels from AI analysis */
	labels: string[];
	/** S3 key for the video file (if available) */
	mediaKey?: string;
	/** Pipeline instance label for multi-instance types (null for single-instance) */
	pipelineLabel?: string;
	/** Pipeline type that produced this measurement (defaults to "anomaly" when missing) */
	pipelineType?: PipelineType;
	/** Sensor coordinates (if available) for geospatial matching */
	sensorCoordinates?: GeoJSONPoint;
	/** Sensor identifier that captured the measurement */
	sensorId: string;
	/** Calculated severity score (1-10) */
	severity: number;
	/** ISO timestamp when the detected event started */
	startTimestamp: string;
	/** Classification type of the measurement */
	type: string;
	/** Zone identifier where the measurement was taken */
	zoneId: string;
}

/**
 * Full EventBridge event structure for MeasurementCreated
 */
export interface MeasurementCreatedEventBridgeEvent {
	detail: MeasurementCreatedEvent;
	"detail-type": "measurement.created";
	source: "measurement.service";
}

// ============================================================================
// Unified Email Event Types
// ============================================================================

/**
 * Event payload for measurement.enhanced events
 * Emitted by VLM service after a second-pass analysis (e.g., collision detection at higher FPS)
 */
export interface MeasurementEnhancedEvent {
	/** Whether the camera was damaged by vandalism */
	cameraDamaged?: boolean;
	/** Whether the camera was obstructed by vandalism */
	cameraObstructed?: boolean;
	/** Whether the enhanced analysis confirmed the detection */
	confirmed?: boolean;
	/** Cause of damage (thrown_rock, thrown_object, physical_grab, struck_with_object, etc.) */
	damageCause?: string;
	/** Updated description from enhanced analysis */
	description?: string;
	/** Flag indicating this is an enhanced measurement */
	enhanced: true;
	/** Reason the measurement was re-analyzed */
	enhancedReason: string;
	/** Which enhanced analysis type this result is for (e.g., "collision", "threat") */
	enhancedType: string;
	/** Types of enhanced analysis applied */
	enhancedTypes: string[];
	/** Type of fire detected (electrical, chemical, structural, unknown) */
	fireType?: string;
	/** Installation identifier */
	installationId: string;
	/** Updated classification labels */
	labels: string[];
	/** S3 key for the video file (used to find original measurement) */
	mediaKey: string;
	/** Pipeline instance label (null for single-instance types like anomaly) */
	pipelineLabel?: string;
	/** Pipeline type that produced this measurement (always "anomaly" today) */
	pipelineType?: PipelineType;
	/** Updated severity score */
	severity: number;
	/** Classification type */
	type: string;
	/** Whether a weapon was detected */
	weaponDetected?: boolean;
	/** Type of weapon detected */
	weaponType?: string;
}

/**
 * Full EventBridge event structure for Measurement events from VLM service
 */
export interface MeasurementEventBridgeEvent {
	detail: Measurement;
	"detail-type": "measurement.new";
	source: typeof VLM_EVENT_SOURCE;
}

/**
 * Event payload for measurement.updated events
 * Emitted by measurement-subscriber after updating a measurement with enhanced data
 */
export interface MeasurementUpdatedEvent {
	/**
	 * Set when the verification behind this update was a backfill over a
	 * historical measurement. The event may be weeks old and its customers
	 * long since told, so the update never notifies, whichever way severity
	 * moved.
	 */
	backfill?: boolean;
	/** Optional description from AI analysis */
	description?: string;
	/** Whether enhanced analysis has been applied */
	enhanced?: boolean;
	/** Per-type results from enhanced analysis passes */
	enhancedResults?: Partial<Record<EnhancedAnalysisType, EnhancedResult>>;
	/** Types of enhanced analysis applied */
	enhancedTypes?: string[];
	/** Unique identifier of the updated measurement */
	id: string;
	/** Installation identifier */
	installationId: string;
	/** Classification labels from AI analysis */
	labels?: string[];
	/** Pipeline instance label (null for single-instance types) */
	pipelineLabel?: string;
	/** Pipeline type that produced this measurement (defaults to "anomaly" when missing) */
	pipelineType?: PipelineType;
	/** Calculated severity score (1-10) */
	severity?: number;
	/**
	 * Present when `updateReason` is `reanalysis` or `reanalysisReverted`: the
	 * event's title, description and types, already composed by the reanalysis
	 * worker (or, on a revert, the ones it had before). The aggregator writes
	 * it instead of regenerating, so one reanalysis costs one summary call
	 * however many measurements it rewrote.
	 */
	summary?: GeneratedEventSummary;
	/** Classification type of the measurement */
	type?: string;
	/**
	 * Why the measurement changed. Absent means enhanced analysis (the original
	 * producer of this event). `verification` means plausibility verification
	 * rewrote severity; consumers may skip work that only enhancement needs.
	 * `reanalysis` means a staff reanalysis rewrote it (`verification` carries
	 * the finding); `reanalysisReverted` means one was undone and the event's
	 * summary has already been restored, so only severity is recomputed.
	 */
	updateReason?:
		| "enhanced"
		| "reanalysis"
		| "reanalysisReverted"
		| "verification";
	/** Present when `updateReason` is `verification` or `reanalysis`. */
	verification?: MeasurementVerification;
}

/**
 * Event payload for notification events (email and SMS)
 * Emitted by NotificationService to trigger email/SMS sending via SQS queues
 */
export interface NotificationEvent {
	channel: NotificationChannel;
	dashboardUrl: string;
	eventDate: string;
	eventDescription: string;
	eventId: string;
	eventSeverity: number;
	eventStatus: string;
	eventTime: string;
	eventTitle: string;
	eventTypes: string[];
	installationId: string;
	locationName: string;
	notificationCount: number;
	notificationLogId: string;
	recipientEmail: string;
	recipientName: string;
	recipientPhone?: string;
	sensors: string;
	sentBy: string;
	sentByUserId?: string;
	tenantId: string;
	zoneName: string;
}

/**
 * Request to generate one sensor's scene description now, out of band from the
 * hourly cron.
 *
 * `installationId` is the EXTERNAL installation id and is carried purely so the
 * handler can resolve which tenant database the sensor lives in — the same
 * mechanism `RawVideoBootstrap` uses. `sensorId` is the internal Mongo id,
 * because the handler operates on the Sensor document directly.
 */
export interface SceneDescriptionRequestedEvent {
	/** External installation id, for tenant resolution. */
	installationId: string;
	/**
	 * `preliminary` describes the camera from its first clip alone and leaves
	 * the 24-hour window open. `regenerate` describes it from the last day of
	 * clips and is terminal. `geometry` rewrites only the camera geometry
	 * paragraph from a recent clip and leaves the description alone.
	 */
	mode: "geometry" | "preliminary" | "regenerate";
	/** Internal sensor id. */
	sensorId: string;
}

/**
 * Full EventBridge event structure for a scene-description request.
 */
export interface SceneDescriptionRequestedEventBridgeEvent {
	detail: SceneDescriptionRequestedEvent;
	"detail-type": typeof SCENE_DESCRIPTION_REQUESTED_DETAIL_TYPE;
	source: typeof SCENE_DESCRIPTION_SOURCE;
}

/**
 * Request to draft one scene-fact proposal in the queue worker.
 *
 * `installationId` is the EXTERNAL installation id, carried so the handler can
 * resolve the tenant database, as for `SceneDescriptionRequestedEvent`.
 * `proposalId` is the internal id of the `drafting` row to fill in.
 */
export interface SceneFactsRequestedEvent {
	/** External installation id, for tenant resolution. */
	installationId: string;
	/** Internal SceneFactProposal id. */
	proposalId: string;
}

/**
 * Full EventBridge event structure for a scene-facts draft request.
 */
export interface SceneFactsRequestedEventBridgeEvent {
	detail: SceneFactsRequestedEvent;
	"detail-type": typeof SCENE_FACTS_REQUESTED_DETAIL_TYPE;
	source: typeof SCENE_FACTS_SOURCE;
}

/**
 * Event payload emitted after a raw on-prem sensor payload is persisted.
 * Type-specific processors subscribe filtered by `sensorType` and load the
 * verbatim payload from `RawSensorData` by `rawSensorDataId` to normalize it.
 */
export interface SensorDataRecordedEvent {
	/** Device-claimed capture time, ISO (when provided). */
	capturedAt?: string;
	/** Source payload format. */
	format: SensorFormat;
	/** External installation ID — used for tenant resolution. */
	installationId: string;
	/** Internal RawSensorData document id. */
	rawSensorDataId: string;
	/** External sensor ID that produced the data. */
	sensorId: string;
	/** Category of sensor data — drives EventBridge routing. */
	sensorType: SensorDataType;
	/**
	 * External id of the co-located video sensor this metadata stream narrates,
	 * as declared by the edge. Measurements derived from this record are
	 * attributed to that sensor so both streams share sensor identity.
	 */
	videoSensorId?: string;
	/** External zone ID, when known. */
	zoneId?: string;
}

/**
 * Full EventBridge event structure for sensor-data.recorded.
 */
export interface SensorDataRecordedEventBridgeEvent {
	detail: SensorDataRecordedEvent;
	"detail-type": typeof SENSOR_DATA_RECORDED_DETAIL_TYPE;
	source: typeof RAW_SENSOR_DATA_SOURCE;
}

export interface UserUpdatedEmailEvent {
	dashboardUrl: string;
	emailType: "user-updated";
	idempotencyKey: string;
	organizationName: string;
	recipientName: string;
	status: "active" | "deactivated";
	tenantId: string;
	userEmail: string;
}

/**
 * Event payload for video motion detected events
 * Extends VideoReadyEvent with motion detection results
 * Emitted when motion is detected in a video, routes to VLM for AI processing
 */
export interface VideoMotionEvent extends VideoReadyEvent {
	/** Timestamp in milliseconds where motion was first detected in the video */
	detectedAtMs: number;
	/** S3 location of the generated thumbnail at the motion detection point */
	thumbnail: {
		/** S3 bucket containing the thumbnail */
		bucket: string;
		/** S3 key path to the thumbnail image */
		key: string;
	};
	/** Video duration in seconds (from ffmpeg) */
	videoDuration?: number;
}

/**
 * Full EventBridge event structure for VideoMotion
 */
export interface VideoMotionEventBridgeEvent {
	detail: VideoMotionEvent;
	"detail-type": typeof VIDEO_MOTION_DETAIL_TYPE;
	source: typeof MOTION_DETECTION_SOURCE;
}

/**
 * Event payload for video no motion detected events
 * Same fields as VideoReadyEvent (no additional fields)
 * Emitted for observability only when no motion is detected
 */
export type VideoNoMotionEvent = VideoReadyEvent;

/**
 * Full EventBridge event structure for VideoNoMotion
 */
export interface VideoNoMotionEventBridgeEvent {
	detail: VideoNoMotionEvent;
	"detail-type": typeof VIDEO_NO_MOTION_DETAIL_TYPE;
	source: typeof MOTION_DETECTION_SOURCE;
}

/**
 * Event payload for video ready events
 * Emitted when a video is ready for AI processing
 */
export interface VideoReadyEvent {
	/** S3 bucket containing the video */
	bucket: string;
	/** Merged configuration from all applicable scopes */
	config: Record<string, unknown>;
	/** Installation identifier */
	installationId: string;
	/** S3 key path to the video file */
	key: string;
	/** ISO timestamp when the pipeline started (video.ready emitted) */
	pipelineStartedAt?: string;
	/** Sensor identifier that captured the video */
	sensorId: string;
	/** Timestamp of the video */
	timestamp: string;
	/** Zone identifier where the video was captured */
	zoneId: string;
}

/**
 * Full EventBridge event structure for VideoReady
 */
export interface VideoReadyEventBridgeEvent {
	detail: VideoReadyEvent;
	"detail-type": typeof VIDEO_READY_DETAIL_TYPE;
	source: typeof VIDEO_EVENT_SOURCE | typeof VIDEO_PROCESSOR_EVENT_SOURCE;
}

/**
 * Event payload for videos requiring preprocessing
 * Emitted when a video needs fisheye correction or other transformations
 */
export interface VideoRequiresPreprocessingEvent {
	/** S3 bucket containing the video */
	bucket: string;
	/** Merged configuration from all applicable scopes */
	config: Record<string, unknown>;
	/** Installation identifier */
	installationId: string;
	/** S3 key path to the video file */
	key: string;
	/** Sensor identifier that captured the video */
	sensorId: string;
	/** Timestamp of the video */
	timestamp: string;
	/** Zone identifier where the video was captured */
	zoneId: string;
}

/**
 * Full EventBridge event structure for VideoRequiresPreprocessing
 */
export interface VideoRequiresPreprocessingEventBridgeEvent {
	detail: VideoRequiresPreprocessingEvent;
	"detail-type": typeof VIDEO_REQUIRES_PREPROCESSING_DETAIL_TYPE;
	source: typeof VIDEO_EVENT_SOURCE;
}

/**
 * Event payload for weapon detected events
 * Emitted when the weapons detection service finds a weapon in a video
 */
export interface WeaponDetectedEvent {
	/** S3 bucket containing the video */
	bucket: string;
	/** Array of detected weapons with labels and confidence scores */
	detections: DetectedWeapon[];
	/** ISO timestamp when the video ended */
	endTimestamp: string;
	/** Installation identifier */
	installationId: string;
	/** S3 key path to the video file */
	key: string;
	/** Highest confidence score across all detections */
	maxConfidence: number;
	/** Measurement ID created for this detection */
	measurementId: string;
	/** Sensor identifier that captured the video */
	sensorId: string;
	/** Severity score (9-10) for the detection */
	severity: number;
	/** ISO timestamp when the video started */
	startTimestamp: string;
	/** Zone identifier where the video was captured */
	zoneId: string;
}

/**
 * Full EventBridge event structure for WeaponDetected
 */
export interface WeaponDetectedEventBridgeEvent {
	detail: WeaponDetectedEvent;
	"detail-type": typeof WEAPON_DETECTED_DETAIL_TYPE;
	source: typeof WEAPONS_DETECTION_SOURCE;
}

/**
 * Event payload for weapon no detection events
 * Emitted for observability when no weapon is found in a video
 */
export interface WeaponNoDetectionEvent {
	/** S3 bucket containing the video */
	bucket: string;
	/** ISO timestamp when the video ended */
	endTimestamp: string;
	/** Installation identifier */
	installationId: string;
	/** S3 key path to the video file */
	key: string;
	/** Sensor identifier that captured the video */
	sensorId: string;
	/** ISO timestamp when the video started */
	startTimestamp: string;
	/** Zone identifier where the video was captured */
	zoneId: string;
}

/**
 * Full EventBridge event structure for WeaponNoDetection
 */
export interface WeaponNoDetectionEventBridgeEvent {
	detail: WeaponNoDetectionEvent;
	"detail-type": typeof WEAPON_NO_DETECTION_DETAIL_TYPE;
	source: typeof WEAPONS_DETECTION_SOURCE;
}
